import React, { useEffect, useState } from 'react';
import Sheets from 'components/Sheets/Sheets';
import { isIkeaApp, isKiosk, isProduction, isWeb } from 'utils/platform';
import Screensaver from 'components/Screensaver/Screensaver';
import { Redirect, Route, Switch } from 'react-router-dom';
import { AppRoutes } from 'routes';
import Planner from 'views/Planner';
import LandingPage from 'views/LandingPage';
import LoadConfigFromSpr from 'components/LoadConfigFromSpr';
import SummaryConfigurationLoader from 'views/Summary/SummaryConfigurationLoader';
import LoadConfigFromVpc from 'components/LoadConfigFromVpc';
import Summary from 'views/Summary';
import { getQuery } from 'utils/queryUtils';
import Guide from 'views/Guide';
import Features from 'components/Features/Features';
import { useDispatch, useSelector } from 'react-redux';
import { isLoadingScreenVisible, setShowBannerMessage } from 'ducks/layout';
import PlannerCrash from 'components/PlannerCrash/PlannerCrash';
import { useServiceSettings } from 'contexts/serviceSettings';
import { useNavigationEvents } from 'hooks/useNavigationEvents';
import {
  anyIntegrationActive,
  getKioskIntegration,
  isSpaceEnabled,
  useIntegrationInteraction
} from 'utils/integration';
import LoadDesignIdFromUrl from 'components/LoadDesignIdFromUrl';
import InfoMessage from 'components/InfoMessage/InfoMessage';
import { getRedirectTarget } from 'redirects';
import { useCore } from 'contexts/core';
import { SettingsComponent } from '@inter-ikea-storageone/range';
import { usePlannerConfig } from 'contexts/plannerConfig';

const IkeaAppRoutes = () => {
  // Beware!
  //   Do not wrap routes in fragments or other components
  //   React router does some dirty things inspecting its children
  //   and does not understand non-direct descendant routes (e.g. routes wrapped in fragments)
  //  https://github.com/remix-run/react-router/issues/5785
  return (
    <Switch>
      <Route path={AppRoutes.PLANNER} />
      <Route path={AppRoutes.SPR} component={LoadConfigFromSpr} />
      <Route path={AppRoutes.VPC} component={LoadConfigFromVpc} />
      <Route path={AppRoutes.DESIGN_ID} component={LoadDesignIdFromUrl} />
      <Route path="/*" render={() => <Redirect to={getRedirectTarget() ?? '/'} />} />
    </Switch>
  );
};

const WebAppRoutes = () => {
  const enableSpacePlanning = isSpaceEnabled();
  // Beware!
  //   Do not wrap routes in fragments or other components
  //   React router does some dirty things inspecting its children
  //   and does not understand non-direct descendant routes (e.g. routes wrapped in fragments)
  //  https://github.com/remix-run/react-router/issues/5785
  return (
    <Switch>
      <Route path={AppRoutes.PLANNER} />
      <Route exact path={AppRoutes.HOME} component={LandingPage} />
      <Route path={AppRoutes.SPR} component={LoadConfigFromSpr} />
      <Route path={AppRoutes.VPC} component={LoadConfigFromVpc} />
      <Route path={AppRoutes.DESIGN_ID} component={LoadDesignIdFromUrl} />
      {!enableSpacePlanning && <Route path={AppRoutes.GUIDE} component={Guide} />}
      {!enableSpacePlanning && (
        <Route path={AppRoutes.SUMMARY_VPC} component={SummaryConfigurationLoader} />
      )}
      {!enableSpacePlanning && <Route path={AppRoutes.SUMMARY} component={Summary} />}
      {!isProduction() && <Route exact path={AppRoutes.CRASH} component={PlannerCrash} />}
      <Route path={AppRoutes.LANDING_PAGE} render={() => <Redirect to={AppRoutes.HOME} />} />
      <Route path="/*" render={() => <Redirect to={getRedirectTarget() ?? '/'} />} />
    </Switch>
  );
};
const PlannerComponent = () => {
  const dispatch = useDispatch();
  const {
    dexfSettings: { general }
  } = useServiceSettings();
  const loadingScreenVisible = useSelector(isLoadingScreenVisible);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const coreApi = useCore();
  const {
    plannerConfig: { screensaverPath, secondaryScreensaverPath }
  } = usePlannerConfig();

  const showScreensaver =
    isKiosk() && !anyIntegrationActive() && (screensaverPath || secondaryScreensaverPath);

  useNavigationEvents();
  useIntegrationInteraction();

  useEffect(() => {
    const kioskIntegration = getKioskIntegration();

    if (!initialLoadDone && !loadingScreenVisible) {
      setInitialLoadDone(true);
      kioskIntegration.applicationLoaded();
      // Upptäcka will ignore handle banner message
      kioskIntegration.handleCountdownBanner();
    }
  }, [loadingScreenVisible, initialLoadDone]);

  useEffect(() => {
    dispatch(
      setShowBannerMessage(
        getQuery().get('enableBannerMessage') === 'true' ||
          (isKiosk() ? general.bannerMessageEnabledKiosk : general.bannerMessageEnabled)
      )
    );

    // Set general in the scene that we need to access in core.
    const scene = coreApi.entities.getScene();
    scene?.addComponent(
      new SettingsComponent({ disableFrameStacking: general.disableFrameStacking })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  return (
    <div onContextMenu={e => !isWeb() && e.preventDefault()}>
      <Sheets />
      <Features />
      <InfoMessage />
      {showScreensaver && <Screensaver />}
      <Planner />
      {isIkeaApp() ? <IkeaAppRoutes /> : <WebAppRoutes />}
    </div>
  );
};

export default PlannerComponent;
